import { useState, useEffect } from 'react';
import { 
  Search, GraduationCap, Plus, Edit, Trash2,
  Loader2, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProgram = {
  title: '',
  description: '',
  type: 'Curso',
  modality: 'En línea',
  duration: '',
  hours: 0,
  institution: '',
  price: 0,
  currency: 'MXN',
  status: 'Borrador'
};

export function AdminCourses() {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [form, setForm] = useState(emptyProgram);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  async function fetchPrograms() {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/academic-programs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCreate() {
    setEditingProgram(null);
    setForm(emptyProgram);
    setIsModalOpen(true);
  }

  function handleEdit(program) {
    setEditingProgram(program);
    setForm({
      title: program.nombre || program.title || '',
      description: program.descripcion || program.description || '',
      type: program.tipo || program.type || 'Curso',
      modality: program.modalidad || program.modality || 'En línea',
      duration: program.duracion || program.duration || '',
      hours: program.horas || program.hours || 0,
      institution: program.institucion || program.institution || '',
      price: program.precio_normal || program.price || 0,
      currency: program.currency || 'MXN',
      status: program.status || 'Borrador'
    });
    setIsModalOpen(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const url = editingProgram 
        ? `${API}/admin/academic-program/${editingProgram.id}`
        : `${API}/admin/academic-program`;
      
      const response = await fetch(url, {
        method: editingProgram ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setIsModalOpen(false);
        fetchPrograms();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error saving program:', error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(program) {
    if (!confirm('¿Eliminar "' + (program.nombre || program.title) + '"?')) return;

    try {
      const response = await fetch(`${API}/admin/academic-program/${program.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  }

  const filteredPrograms = programs.filter(function(p) {
    const matchesSearch = !search || 
      (p.nombre && p.nombre.toLowerCase().includes(search.toLowerCase())) ||
      (p.title && p.title.toLowerCase().includes(search.toLowerCase())) ||
      (p.institucion && p.institucion.toLowerCase().includes(search.toLowerCase())) ||
      (p.institution && p.institution.toLowerCase().includes(search.toLowerCase()));
    const matchesType = !typeFilter || typeFilter === 'all' || p.tipo === typeFilter || p.type === typeFilter;
    return matchesSearch && matchesType;
  });

  function renderProgramCard(program) {
    const isPublished = program.status === 'Publicado';
    const nombre = program.nombre || program.title || 'Sin nombre';
    const tipo = program.tipo || program.type || 'Curso';
    const institucion = program.institucion || program.institution || '-';
    const horas = program.horas || program.hours || 0;
    const modalidad = program.modalidad || program.modality || '-';
    const precio = program.precio_normal || program.price || 0;

    return (
      <div key={program.id} className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6 hover:border-[#3a3a3a] transition-colors">
        <div className="flex items-start justify-between mb-4">
          <div>
            <Badge className={isPublished ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}>{program.status}</Badge>
            <Badge className="ml-2 bg-[#2a2a2a] text-gray-300">{tipo}</Badge>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => handleEdit(program)} className="text-gray-400 hover:text-white hover:bg-[#2a2a2a]">
              <Edit className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => handleDelete(program)} className="text-gray-400 hover:text-red-400 hover:bg-red-500/10">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <h3 className="text-white font-medium mb-2">{nombre}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-400">
            <GraduationCap className="w-4 h-4" />{institucion}
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Clock className="w-4 h-4" />{horas} horas | {modalidad}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
          <p className="text-lg font-semibold text-white">${precio.toLocaleString('es-MX')} MXN</p>
        </div>
      </div>
    );
  }

  const cards = [];
  for (let i = 0; i < filteredPrograms.length; i++) {
    cards.push(renderProgramCard(filteredPrograms[i]));
  }

  return (
    <div className="p-8" data-testid="admin-courses">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-manrope font-bold text-2xl text-white mb-2">Oferta Académica</h1>
          <p className="text-gray-400">Gestiona cursos, diplomados y programas del Colegio</p>
        </div>
        <Button onClick={handleCreate} className="bg-white text-black hover:bg-gray-200">
          <Plus className="w-4 h-4 mr-2" />Nuevo Programa
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Buscar por título o institución..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#111111] border-[#2a2a2a] text-white" />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v)}>
          <SelectTrigger className="w-full md:w-48 bg-[#111111] border-[#2a2a2a] text-white">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="Curso">Curso</SelectItem>
            <SelectItem value="Diplomado">Diplomado</SelectItem>
            <SelectItem value="Certificación">Certificación</SelectItem>
            <SelectItem value="Licenciatura">Licenciatura</SelectItem>
            <SelectItem value="Maestría">Maestría</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>
      ) : cards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>
      ) : (
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-12 text-center">
          <GraduationCap className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No se encontraron programas</p>
          <Button onClick={handleCreate} className="mt-4 bg-white text-black hover:bg-gray-200">Crear Primer Programa</Button>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#111111] border-[#2a2a2a] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editingProgram ? 'Editar Programa' : 'Nuevo Programa'}</DialogTitle></DialogHeader>
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-gray-300">Título</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Nombre del programa" />
              </div>
              <div>
                <Label className="text-gray-300">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                    <SelectItem value="Curso">Curso</SelectItem>
                    <SelectItem value="Diplomado">Diplomado</SelectItem>
                    <SelectItem value="Certificación">Certificación</SelectItem>
                    <SelectItem value="Licenciatura">Licenciatura</SelectItem>
                    <SelectItem value="Maestría">Maestría</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Modalidad</Label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                    <SelectItem value="En línea">En línea</SelectItem>
                    <SelectItem value="Presencial">Presencial</SelectItem>
                    <SelectItem value="Híbrido">Híbrido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Institución</Label>
                <Input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Nombre de la institución" />
              </div>
              <div>
                <Label className="text-gray-300">Duración</Label>
                <Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Ej: 3 meses" />
              </div>
              <div>
                <Label className="text-gray-300">Horas</Label>
                <Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: parseInt(e.target.value) || 0 })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Precio</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                    <SelectItem value="Borrador">Borrador</SelectItem>
                    <SelectItem value="Publicado">Publicado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-300">Descripción</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Descripción del programa..." rows={4} />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-[#2a2a2a] text-gray-400">Cancelar</Button>
              <Button onClick={handleSave} disabled={isSaving || !form.title} className="bg-white text-black hover:bg-gray-200">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingProgram ? 'Guardar Cambios' : 'Crear Programa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
